import { Component, ElementRef, ViewChild, OnInit, OnDestroy, Optional, Inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { Location } from '@angular/common';
import { filter, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StaffServiceService } from '../staff/service/staff-service.service';
import { EventEmmiterService } from 'src/app/services/event-emmiter.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-ai-writer-wrapper',
  template: `
    <div class="wrapper-container">
      <div *ngIf="!iframeLoaded" class="loader-overlay">
        <img src="assets/animations/animation.gif" alt="Loading" width="10%" />
      </div>
      <iframe
        #appIframe
        [src]="aiWriterUrl"
        (load)="onIframeLoad()"
        (error)="onIframeError()"
        width="100%"
        height="100%"
        style="border: none;"
        title="AI Writer"
        loading="lazy"
        allow="geolocation; microphone; camera; clipboard-write; clipboard-read"
        [style.visibility]="iframeLoaded ? 'visible' : 'hidden'"
      ></iframe>
    </div>
  `,
  styles: [
    `
      .wrapper-container {
        position: relative;
        width: 100%;
        height: 100%;
        min-height: calc(100vh - 64px);
      }
      .loader-overlay {
        position: absolute;
        inset: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        background: #fff;
        z-index: 10;
      }
    `,
  ],
})
export class AiWriterWrapperComponent implements OnInit, OnDestroy {
  @ViewChild('appIframe') iframe!: ElementRef<HTMLIFrameElement>;

  aiWriterUrl!: SafeResourceUrl;
  iframeLoaded = false;
  iframeReady = false;
  sendData: any

  private messageListener!: (e: MessageEvent) => void;
  private navSub?: Subscription;
  private aiWriterSub?: Subscription;

  constructor(
    @Optional() public dialogRef: MatDialogRef<AiWriterWrapperComponent>,
    private router: Router,
    private location: Location,
    private sanitizer: DomSanitizer,
    private staffService: StaffServiceService,
    private eventEmitter: EventEmmiterService,
    @Optional() @Inject(MAT_DIALOG_DATA)
    public dialogData: any,
  ) {
    this.setData();
  }

  ngOnInit(): void {
    const base = environment.aiWriter.replace(/\/?$/, '/');
    const path = this.location.path();
    if (!this.dialogRef && path.toLowerCase().startsWith('/admin/ai-writer')) {
      const cleanPath = path.replace(/^\//, '');
      this.aiWriterUrl = this.sanitizer.bypassSecurityTrustResourceUrl(base + cleanPath);
    } else {
      this.aiWriterUrl = this.sanitizer.bypassSecurityTrustResourceUrl(base);
    }

    if (!this.hasPendingRoute()) {
      setTimeout(() => {
        this.iframeLoaded = true;
      }, 1000);
    }

    this.messageListener = (event: MessageEvent) => {
      if (event.data?.type === 'route_change' && event.data.path) {
        if (!this.dialogRef && window.location.pathname !== event.data.path) {
          this.location.go(event.data.path);
        }
        const path = event.data.path.split('?')[0];
        if (
          path.includes('social-media-writer') ||
          path.includes('blog-writer') ||
          path.includes('email-writer')
        ) {
          this.iframeLoaded = true;
        }
      }
      if (event.data?.type === 'iframe_ready') {
        const hadPendingRoute = this.hasPendingRoute();
        this.iframeReady = true;
        this.pushRoutingToIframe();
        if (!hadPendingRoute) {
          this.iframeLoaded = true;
        }
      }
      if (event.data?.type === 'post_created') {
        this.router.navigate(['/admin/marketing/list-posts'])
        this?.dialogRef?.close()
        this.eventEmitter.aiWriterClosed.next({ data: { callApi: true } })
      }
      if (event.data?.type === 'idea_created') {
        this.router.navigate(['/admin/marketing/list-ideas'])
        this?.dialogRef?.close()
        this.eventEmitter.aiWriterClosed.next({ data: { callApi: true } })
      }
      if (event.data?.type === 'blog_created') {
        this.router.navigate(['/admin/website/blogs/listing'])
        this?.dialogRef?.close()
      }
      if (event.data?.type === 'blog_back') {
        this?.dialogRef?.close()
      }
      if (event.data?.type === 'link_channels') {
        this.router.navigate(['/admin/marketing/smm-settings/list-channels'])
        this?.dialogRef?.close()
      }
    };
    window.addEventListener('message', this.messageListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageListener);
    if (localStorage.getItem('iframeOpened') === 'true') {
      localStorage.removeItem('iframeOpened')
    }
    // this.navSub?.unsubscribe();
    this.aiWriterSub?.unsubscribe();
  }

  onIframeError(): void {
    this.iframeLoaded = true;
  }

  setData() {
    this.aiWriterSub = this.eventEmitter.aiWriterCalled.subscribe(
      (res: any) => {
        if (!res) return;
        if (res.data.toOpen === 'post') {
          if (res.data.type === 'edit-idea') {
            localStorage.setItem('openPostIdea', 'openPostIdea')
            this.sendData = res.data.sendData
            localStorage.setItem("sendData", JSON.stringify(this.sendData));
          }
          else if (res.data.type === 'edit-prism') {
            localStorage.setItem('openPostPrism', 'openPostPrism')
            this.sendData = res.data.sendData
            localStorage.setItem("sendData", JSON.stringify(this.sendData));
          }
          else if (res.data.type === 'new-post') {
            localStorage.setItem('openPost', 'openPost')
            this.sendData = res.data.sendData || null
            if (this.sendData) {
              localStorage.setItem("sendData", JSON.stringify(this.sendData));
            } else {
              localStorage.removeItem('sendData');
            }
          }
        }
        if (res.data.toOpen === 'blog') {
          localStorage.setItem('openBlog', 'openBlog')
        }

        if (this.iframeReady) {
          this.pushRoutingToIframe();
        }

        // Reset behavior subject
        this.eventEmitter.aiWriterCalled.next(null);
      }
    );
  }

  onIframeLoad(): void {
    this.pushContextToIframe();
    if (this.hasPendingRoute()) {
      // Push the target writer route as early as possible so the default landing view never wins.
      this.pushRoutingToIframe(false);
      return;
    }

    if (!this.hasPendingRoute()) {
      this.iframeLoaded = true;
    }
  }

  hasPendingRoute(): boolean {
    return !!(
      localStorage.getItem('openPost') ||
      localStorage.getItem('openPostIdea') ||
      localStorage.getItem('openPostPrism') ||
      localStorage.getItem('openBlog')
    );
  }

  private pushContextToIframe(): void {
    const win = this.iframe?.nativeElement?.contentWindow;
    if (!win) return;

    // Setting key in local to return so that it does not call again for loading the post writer from socialflow
    if (localStorage.getItem('iframeOpened') === 'true') {
      return;
    }

    const bDetails = localStorage.getItem('bDetails');
    const staffType = localStorage.getItem('staffType');
    const staffId = localStorage.getItem('staffId');
    const staffName = localStorage.getItem('staffName');
    const staffEmail = localStorage.getItem('staffEmail');
    const businessId = localStorage.getItem('businessId');
    const business = localStorage.getItem('business');
    const mobileLogin = localStorage.getItem('mobileLogin');
    const path = this.location.path();
    localStorage.setItem('iframeOpened', 'true')

    const isAdmin = this.staffService.isStaffAdmin();
    win.postMessage({ type: 'isAdmin', data: JSON.stringify(isAdmin) }, '*');
    win.postMessage({ type: 'set_path', path }, '*');
    win.postMessage({ type: 'localStorage', data: JSON.stringify(bDetails) }, '*');
    win.postMessage({ type: 'staffType', data: JSON.stringify(staffType) }, '*');
    win.postMessage({ type: 'staffId', data: JSON.stringify(staffId) }, '*');
    win.postMessage({ type: 'staffName', data: JSON.stringify(staffName) }, '*');
    win.postMessage({ type: 'staffEmail', data: JSON.stringify(staffEmail) }, '*');
    win.postMessage({ type: 'businessId', data: JSON.stringify(businessId) }, '*');
    win.postMessage({ type: 'business', data: JSON.stringify(business) }, '*');
    win.postMessage({ type: 'mobileLogin', data: JSON.stringify(mobileLogin) }, '*');
  }

  private pushRoutingToIframe(clearAfterSend: boolean = true): void {
    const win = this.iframe?.nativeElement?.contentWindow;
    if (!win) return;

    if (localStorage.getItem('openPost')) {
      const sendData = JSON.parse(localStorage.getItem("sendData") || "null");
      win.postMessage({ type: 'open_post_writer', data: { create: true, sendData }, }, '*');
      if (clearAfterSend) {
        localStorage.removeItem('openPost')
        localStorage.removeItem('sendData')
      }
    }
    if (localStorage.getItem('openPostIdea')) {
      const sendData = JSON.parse(localStorage.getItem("sendData") || "null");
      win.postMessage({ type: 'open_post_writer', data: { idea: true, sendData: sendData }, }, '*');
      if (clearAfterSend) {
        localStorage.removeItem('openPostIdea')
        localStorage.removeItem('sendData')
      }
    }
    if (localStorage.getItem('openPostPrism')) {
      const sendData = JSON.parse(localStorage.getItem("sendData") || "null");
      win.postMessage({ type: 'open_post_writer', data: { idea: true, sendData: sendData }, }, '*');
      if (clearAfterSend) {
        localStorage.removeItem('openPostPrism')
        localStorage.removeItem('sendData')
      }
    }
    if (localStorage.getItem('openBlog')) {
      win.postMessage({ type: 'open_blog_writer', data: { create: true }, }, '*');
      if (clearAfterSend) {
        localStorage.removeItem('openBlog')
      }
    }
  }
}
